import cn from 'classnames';
import differenceInDays from 'date-fns/differenceInDays';
import { useRouter } from 'next/router';
import React, { memo, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { Icon, IconButton } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import Modal from 'components/Modal';
import { Form } from 'components/form';
import { Col, Row } from 'components/layout';

import { useEvent } from 'hooks/useEvent';
import { useNotification } from 'hooks/useNotification';

import { OpportunityAttributes } from 'lib/models/opportunity';
import { TouchpointType } from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';
import { REGEX } from 'lib/utils/regex';

import { useAttendeesFilter } from '../useAttendeesFilter';

import styles from '../PendingNotification.module.scss';

export interface PendingLinkNotificationProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  touchpoint: OpportunityAttributes;
}

export const PendingLinkNotification: React.FC<PendingLinkNotificationProps> =
  memo(
    ({
      className = '', // custom class name
      style, // custom style
      touchpoint,
    }: PendingLinkNotificationProps) => {
      const router = useRouter();
      const notificationInstance = useNotification();
      const [isModalOpen, setModalOpen] = useState(false);
      const { id, title, start_date, creatable_for } = touchpoint;
      const { onAddEventLink } = useEvent();
      const { mutateTouchpoint } = useAttendeesFilter();

      const [t] = useTranslation('event-dashboard');

      const isSocietyEvent = useMemo(() => {
        return creatable_for?.data.type === 'student_society';
      }, [creatable_for]);

      const editLink = useMemo(() => {
        return `${(isSocietyEvent
          ? PAGE_ROUTES.EVENTS_EDIT
          : PAGE_ROUTES.CAMPAIGNS_EDIT
        ).replace('[id]', id as string)}?step=1&type=${TouchpointType.Event}`;
      }, [id, isSocietyEvent]);

      const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        watch,
      } = useForm();

      const onlineLink = watch('online_link');

      const onSubmit: SubmitHandler<{ online_link: string }> = async (data) => {
        const response = await onAddEventLink({ id, ...data });
        if (response) {
          mutateTouchpoint();
          setModalOpen(false);
          notificationInstance.success({
            title: t('meeting-link.added.title'),
            message: t('meeting-link.added.message'),
          });
        }
      };

      const diff = differenceInDays(new Date(start_date), new Date());

      return (
        <div
          className={cn(
            {
              [styles.PendingNotification]: true,
            },
            { [styles.PendingNotificationWarning]: diff < 3 },
            className
          )}
          style={style}
          data-testid="PendingLinkNotification"
        >
          <Row align="start" wrap={false} className={styles.header}>
            <Col>
              <Icon iconName="icon_info" size="large" />
            </Col>{' '}
            <Col>
              <p
                className={cn(styles.title, {
                  [styles.titleWarning]: diff < 3,
                })}
              >
                Meeting link pending
              </p>
              {diff < 3 ? (
                <p className={cn(styles.desc, styles.descWarning)}>
                  <mark>{t('meeting-link.message-1-mark', { title })}</mark>{' '}
                  {t('meeting-link.message-1')}
                </p>
              ) : (
                <p className={styles.desc}>{t('meeting-link.message-2')}</p>
              )}
            </Col>
          </Row>
          <ButtonV3 onClick={() => setModalOpen(true)} color="secondary">
            {t('meeting-link.button')}
          </ButtonV3>

          {isModalOpen ? (
            <Modal
              isOpen={isModalOpen}
              onRequestClose={() => setModalOpen(false)}
              className={styles.modal}
              contentClassName={styles.modalContent}
            >
              <IconButton
                size="xsmall"
                iconName="close"
                className={styles.cancel}
                onClick={() => setModalOpen(false)}
              />
              <Form
                autoComplete="off"
                onSubmit={handleSubmit(onSubmit)}
                style={style}
                data-testid="PendingLinkNotification"
              >
                <div className={styles.header}>
                  <p className={styles.title}>
                    {t('meeting-link.modal.title')}
                  </p>
                  <p className={styles.desc}>
                    {t('meeting-link.modal.message')}
                  </p>
                  <InputV2
                    autoFocus={true}
                    className={styles.EventsNewEventUrl}
                    type="url"
                    label={t('meeting-link.modal.input.label')}
                    placeholder={t('meeting-link.modal.input.placeholder')}
                    {...register('online_link', {
                      pattern: {
                        value: REGEX.URL,
                        message: 'Enter a valid url',
                      },
                    })}
                    isInvalid={!!errors.online_link}
                    helperText={errors.online_link?.message}
                    labelClassName={styles.inputLabel}
                  />
                </div>
                <div className={styles.separator}></div>
                <Row align="center" justify="end" className={styles.buttons}>
                  <ButtonV3
                    type="button"
                    color="secondary"
                    borderless
                    onClick={() => {
                      router.push(editLink);
                    }}
                  >
                    {t('meeting-link.modal.button-1')}
                  </ButtonV3>
                  <ButtonV3
                    type="submit"
                    disabled={!onlineLink || isSubmitting}
                    isLoading={isSubmitting}
                    borderless
                  >
                    {t('meeting-link.modal.button-2')}
                  </ButtonV3>
                </Row>
              </Form>
            </Modal>
          ) : null}
        </div>
      );
    }
  );

PendingLinkNotification.displayName = 'PendingLinkNotification';
